// components/ForcePinReset.tsx
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setSecurityPin, hasSecurityPin } from '../services/userService';
import { Shield, AlertCircle, CheckCircle, RefreshCw, Lock } from 'lucide-react';

const ForcePinReset: React.FC = () => {
  const { user } = useAuth();
  const [step, setStep] = useState<'info' | 'reset'>('info');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleResetPin = async () => {
    if (pin.length !== 4) {
      setMessage({ type: 'error', text: 'PIN must be exactly 4 digits' });
      return;
    }

    if (pin !== confirmPin) {
      setMessage({ type: 'error', text: 'PINs do not match' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await setSecurityPin({ pin });
      
      // Vérifier que le PIN a bien été défini
      const pinExists = await hasSecurityPin();
      
      if (pinExists) {
        setMessage({ 
          type: 'success', 
          text: 'PIN reset successfully! You can now make transfers.' 
        });
        
        // Rediriger vers les transferts après un délai
        setTimeout(() => {
          window.location.href = '/transfers';
        }, 2000);
      } else {
        throw new Error('PIN was not set correctly');
      }
    } catch (error: any) {
      console.error('PIN reset error:', error);
      setMessage({ 
        type: 'error', 
        text: `Error: ${error.response?.data?.detail || error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === 'info') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              PIN Reset Required
            </h1>
            <p className="text-gray-600 text-lg mb-6">
              You need to set a new security PIN to continue using transfer features.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800 text-sm font-medium">
                🔒 Your previous PIN has been invalidated or you need to set a new one.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => setStep('reset')}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-4 rounded-xl font-bold text-lg hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg"
            >
              Set New PIN
            </button>
            
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="w-full text-gray-600 py-3 rounded-xl font-medium border-2 border-gray-300 hover:bg-gray-50 transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Set New PIN
          </h1>
          <p className="text-gray-600">
            Create a new 4-digit security PIN for your account
          </p>
        </div>

        {message && (
          <div className={`p-4 rounded-xl mb-6 flex items-center ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
              Enter New 4-digit PIN
            </label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center text-3xl font-mono tracking-widest transition-all duration-200"
              maxLength={4}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
              Confirm New PIN
            </label>
            <input
              type="password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center text-3xl font-mono tracking-widest transition-all duration-200"
              maxLength={4}
            />
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => setStep('info')}
              disabled={loading}
              className="flex-1 text-gray-600 py-3 rounded-xl font-medium border-2 border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={handleResetPin}
              disabled={loading || pin.length !== 4 || confirmPin.length !== 4}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center shadow-lg"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                  Setting PIN...
                </>
              ) : (
                'Set PIN'
              )}
            </button>
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-blue-800 text-sm text-center">
            <strong>Note:</strong> This PIN will be required for all financial transactions and sensitive operations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForcePinReset;